import { formatVndMinor } from "../money-parser.js";

/**
 * 🔴🔴 US-AIV2-0112 H05 (@UC0322) — FACT TIỀN của lời chào: kiểu dữ liệu + cách NÓI, không một phép tính tiền nào.
 *
 * Trước H05, luật PK-4 của thư viện thẻ là *"thẻ không mang số tiền"* — đúng ở lý do (số tiền lên màn hình phải đi từ fact của API,
 * `@UC0151`), nhưng hậu quả là lời chào chỉ nói được *"Bạn còn 1 khoản cần đóng"*: người đọc phải mở thêm một màn để biết bao nhiêu,
 * hạn khi nào (đo 28/09, persona `tu`). H05 giữ nguyên LÝ DO và đổi CÁCH giữ nó:
 *
 *  1. **Nguồn**: loader (`journey-context.loader.ts`) đọc CÙNG cửa GUI mà màn hình đọc — khoản của tôi = CÙNG hàng `member_dues` mà
 *     `myUnpaidDueCount` đếm (số tiền còn thiếu qua `outstandingMinorOf`, hàm duy nhất của mọi bề mặt); tiến độ đợt = `FundCollectionProgress
 *     Service.listByGroup` (cửa `GET /v1/groups/:id/collection-progress`, mẫu số đã loại `waived` — US-E6.F8-0862, redaction theo vai).
 *     Không `SUM` ledger, không cơ chế thứ hai.
 *  2. **Domain chỉ FORMAT**: mỗi con số đi ra là một con số đi vào (`formatVndMinor` — không cộng, không trừ, không chia). Hai khoản ⇒ nói
 *     SỐ khoản + khoản gần hạn nhất, KHÔNG cộng tổng (một phép cộng tiền ở tầng lời chào là công thức tiền thứ hai).
 *  3. **Ẩn là `•••`, không `0`** (§3.8). Đơn vị không phải VND ⇒ không in số tiền (in "đ" cho USD là nói dối về tiền, §3.1).
 *  4. **Thẻ vẫn không VIẾT số tiền** — `templateVi` chỉ mang chỗ trống (`{chiTietKhoan}` · `{tienDoDot}`), giá trị chèn lúc render từ
 *     fact. Lời chào không đi qua polish (`NO_POLISH_BY_TURN_TYPE.greeting = true`) ⇒ LLM không thấy, không viết lại một con số nào.
 */

/** §3.8: số bị ẩn là `'•••'`, KHÔNG `0`. Cùng chuỗi với trang Xem đợt thu (`READ_COLLECTIONS_TEXT.redactedAmount`). */
export const GUIDE_REDACTED_MONEY = "•••";

/** `formatVndMinor` là VND — đơn vị khác KHÔNG có câu đúng nào để in số tiền. */
const GUIDE_MONEY_CURRENCY = "VND";

/** Khoản CÒN PHẢI ĐÓNG gần hạn nhất của CHÍNH người hỏi (một hàng trong đúng tập mà `myUnpaidDueCount` đếm). */
export interface GuideMyDueFact {
  /** Tên đợt (hoặc tên quỹ khi đợt không tên) — chuỗi người dùng đặt, chuẩn hoá lúc render. */
  readonly title: string;
  /** Còn thiếu (minor, chuỗi số nguyên) — server tính bằng `outstandingMinorOf`, domain KHÔNG tính lại. */
  readonly outstandingMinor: string;
  readonly currency: string;
  /** Số ngày tới hạn — `daysLeftOf` của `collection-progress.math` (một nguồn); `null` = đợt không có hạn. */
  readonly daysLeft: number | null;
  /**
   * 🔴 US-AIV2-tourguide-0004 (review M1) — khoản này có phải khoản GẦN HẠN NHẤT trong MỌI khoản còn phải đóng không. Loader chọn khoản ở đợt
   * ĐANG MỞ trước (nút «Báo đã chuyển» chỉ khai được đợt mở) ⇒ khi còn khoản treo ở đợt đã đóng sổ có hạn sớm hơn, nói "gần hạn nhất" là sai
   * ⇒ `false` ⇒ câu nói "trong đó có". Vắng ⇒ coi như đúng (hành vi cũ).
   */
  readonly nearestOfAll?: boolean;
  /** US-AIV2-tourguide-0004 (review M1) — khoản ở đợt ĐANG MỞ (nút tự báo khai được) hay đợt đã đóng sổ. Vắng ⇒ đợt mở. */
  readonly inOpenCollection?: boolean;
  /**
   * 🔴 UX-GUIDE-POLISH MED-6 (@UC0322) — tên SUẤT (bé) của khoản, CHỈ khi tên đợt TRÙNG với khoản của một suất khác mà chính người hỏi đại diện (loader
   * quyết — luật tên `memberNameOf`, P5.3: suất của mình). Vắng ⇒ câu cũ. Tên do người dùng đặt ⇒ chuẩn hoá lúc render (`cards.ts`).
   */
  readonly seatName?: string;
  /**
   * 🔴 W-5 UX-MANAGER-SELF-PAY (@UC0377 · N-5) — id KHOẢN (`member_dues.id`) mà câu nêu tên: đối tượng của LỐI ĐÚNG khi nút chính không làm được
   * (`DUTY-MY-UNPAID` · `doors` — chủ nhóm/quản trị/thủ quỹ ở quỹ không nhận tự báo tự «Ghi nhận đã đóng» CHÍNH khoản này). Id, không phải tên — không
   * vào câu. Vắng ⇒ không lối đúng (câu không nút như cũ).
   */
  readonly memberDueId?: string;
}

/** Tiến độ đợt đang mở gần nhất — đọc từ cửa GUI `GET /v1/groups/:id/collection-progress` (đã áp redaction theo vai). */
export interface GuideCollectionProgressFact {
  /**
   * 🔴 US-AIV2-tourguide-0003 (@UC0361) — id ĐỢT mà câu tiến độ nói tới: nút "Nhắc người chưa đóng" mang đúng đợt này (K12) và
   * `DEBT-REMIND.ready` kiểm lại đúng đợt này (K11). Id, không phải tên — không vào câu.
   */
  readonly collectionId: string;
  readonly title: string;
  /** Hai số NGƯỜI của cửa GUI (`tallyMemberProgress`: `waived` ra khỏi mẫu số) — in nguyên. */
  readonly paidMembers: number;
  readonly totalMembers: number;
  /**
   * 🔴 review #1064 H1 — số suất mà nút "Nhắc người chưa đóng" SẼ nhắc: `SendRemindersService.describeRemindScope(...).unpaidSeatIds.length`
   * (CÙNG vị-từ của nút: chỉ `pending`/`overdue`). KHÔNG suy từ `isFullyCollected`/`paid < total`: đợt mà người còn lại toàn "đã báo
   * chuyển, chờ đối chiếu" hoặc "đóng dở" vẫn chưa đủ, nhưng nút trả `NO_UNPAID_MEMBER` ⇒ lời mời nhắc thành ngõ cụt. Loader chỉ nạp fact
   * cho đợt có số này > 0.
   */
  readonly remindableSeats: number;
  readonly daysLeft: number | null;
  /**
   * 🔴 US-AIV2-tourguide-0006 (@UC0366 · Q7) — lần nhắc TAY gần nhất của đợt này còn trong khoảng "chưa mời nhắc lại" (`GUIDE_REMIND_AGAIN_DAYS`):
   * giờ nhắc và giờ bắt đầu được mời lại, ĐÃ format theo giờ Việt Nam ở tầng có đồng hồ (loader). Vắng/`null` ⇒ không trong khoảng đó
   * (hoặc không đọc được ⇒ câu không nói "đã nhắc"). Nhắc tự động của worker KHÔNG bao giờ ở đây.
   */
  readonly manualRemind?: { readonly at: string; readonly againFrom: string } | null;
  /** Nhánh `redacted` của cửa GUI giữ nguyên hình: ẩn thì KHÔNG có field tiền nào để đọc nhầm thành 0. */
  readonly money:
    | { readonly redacted: true }
    | { readonly redacted: false; readonly currency: string; readonly collectedMinor: string; readonly expectedMinor: string };
}

/**
 * Cách NÓI một hạn từ `daysLeft` — MỘT chỗ cho lời chào và trang Xem đợt thu (`read-collections.binding.ts` gọi hàm này).
 * Chuỗi rỗng khi không có hạn.
 */
export function dueInPhraseVi(daysLeft: number | null): string {
  if (daysLeft === null) return "";
  if (daysLeft < 0) return `quá hạn ${String(-daysLeft)} ngày`;
  if (daysLeft === 0) return "hạn hôm nay";
  return `còn ${String(daysLeft)} ngày`;
}

function dueTail(daysLeft: number | null): string {
  const phrase = dueInPhraseVi(daysLeft);
  return phrase === "" ? "" : `, ${phrase}`;
}

function moneyOf(minor: string, currency: string): string | null {
  if (currency.trim() !== GUIDE_MONEY_CURRENCY || !/^\d+$/.test(minor)) return null;
  return formatVndMinor(BigInt(minor));
}

/** "Quỹ lớp học kỳ 1 — 500.000 đ, còn 17 ngày". Tên đã chuẩn hoá do caller truyền (luật chuẩn hoá ở `cards.ts`). */
export function renderMyDueDetail(fact: GuideMyDueFact, title: string): string {
  const money = moneyOf(fact.outstandingMinor, fact.currency);
  return `${title}${money === null ? "" : ` — ${money}`}${dueTail(fact.daysLeft)}`;
}

/** "Quỹ lớp học kỳ 1: 3/8 người đã đóng, đã thu 1.500.000 đ / 4.000.000 đ, còn 17 ngày". */
export function renderCollectionProgress(fact: GuideCollectionProgressFact, title: string): string {
  const people = `${String(fact.paidMembers)}/${String(fact.totalMembers)} người đã đóng`;
  let money = "";
  if (fact.money.redacted) money = `, đã thu ${GUIDE_REDACTED_MONEY}`;
  else {
    const collected = moneyOf(fact.money.collectedMinor, fact.money.currency);
    const expected = moneyOf(fact.money.expectedMinor, fact.money.currency);
    if (collected !== null && expected !== null) money = `, đã thu ${collected} / ${expected}`;
  }
  return `${title}: ${people}${money}${dueTail(fact.daysLeft)}`;
}
